import type * as interfaces from '../ts_interfaces/index.js';

/** Hard ceiling for a single composed stack. */
export const maximumStackItems = 40;

/** Canonical list prices used by both the console and the quote service. */
export const CATALOG = {
  shared: 7.5,
  dedicated: 15,
  mem: 2.2,
  blk: 0.11,
  obj: 0.018,
  ip: 2.5,
  node: 990,
  outbound: 0.01,
} as const;

export interface IQuoteRegion {
  id: string;
  code: string;
  city: string;
  state: 'live' | 'build';
  note?: string;
  eta?: string;
}

export const REGIONS: IQuoteRegion[] = [
  { id: 'bre1', code: 'BRE1', city: 'Bremen, DE', state: 'live', note: 'own facility' },
  { id: 'fra1', code: 'FRA1', city: 'Frankfurt, DE', state: 'build', eta: 'Q3 2026' },
  { id: 'ams1', code: 'AMS1', city: 'Amsterdam, NL', state: 'build', eta: 'Q4 2026' },
  { id: 'par1', code: 'PAR1', city: 'Paris, FR', state: 'build', eta: 'Q1 2027' },
  { id: 'sto1', code: 'STO1', city: 'Stockholm, SE', state: 'build', eta: 'Q2 2027' },
  { id: 'waw1', code: 'WAW1', city: 'Warsaw, PL', state: 'build', eta: '2027' },
];

export const DEMAND = {
  moderate: { idx: 1.15, util: 0.78, tag: 'Demand elevated' },
  high: { idx: 1.45, util: 0.94, tag: 'Capacity constrained' },
  critical: { idx: 1.9, util: 0.99, tag: 'Capacity critical' },
} as const;

export const activeDemandKey = 'high';
export const commitmentTerms = [0, 0.1, 0.2, 0.35] as const;

const itemTypes: interfaces.TQuoteItemType[] = ['container', 'node', 'block', 'object', 'ip'];
const coreTypes: interfaces.TQuoteCoreType[] = ['shared', 'dedicated'];

const roundMoney = (valueArg: number): number => Math.round(valueArg * 100) / 100;

const requireInteger = (
  valueArg: unknown,
  labelArg: string,
  minimumArg: number,
  maximumArg: number,
  stepArg = 1,
): number => {
  if (
    typeof valueArg !== 'number'
    || !Number.isSafeInteger(valueArg)
    || valueArg < minimumArg
    || valueArg > maximumArg
    || (valueArg - minimumArg) % stepArg !== 0
  ) {
    throw new Error(`${labelArg} is out of range.`);
  }
  return valueArg;
};

const requireRegion = (valueArg: unknown): IQuoteRegion => {
  if (typeof valueArg !== 'string') {
    throw new Error('Stack item region is unknown.');
  }
  const region = REGIONS.find((candidateArg) => candidateArg.id === valueArg);
  if (!region) {
    throw new Error('Stack item region is unknown.');
  }
  return region;
};

const requireCommitmentTerm = (valueArg: unknown): number => {
  if (
    typeof valueArg !== 'number'
    || !commitmentTerms.some((candidateArg) => candidateArg === valueArg)
  ) {
    throw new Error('Commitment term is unknown.');
  }
  return valueArg;
};

/** Strictly validates one visitor selection and derives its server-owned fields. */
export const calculateQuoteLine = (selectionArg: unknown): interfaces.IQuoteStackItem => {
  if (!selectionArg || typeof selectionArg !== 'object' || Array.isArray(selectionArg)) {
    throw new Error('Stack item is malformed.');
  }
  const raw = selectionArg as Record<string, unknown>;
  const type = itemTypes.find((candidateArg) => candidateArg === raw.type);
  if (!type) {
    throw new Error('Stack item type is unknown.');
  }
  const region = requireRegion(raw.region);
  const item: interfaces.IQuoteStackItem = {
    type,
    region: region.id,
    regionState: region.state,
    baseMonthly: 0,
  };

  switch (type) {
    case 'container': {
      const coreType = coreTypes.find((candidateArg) => candidateArg === raw.coreType);
      if (!coreType) {
        throw new Error('Container core type is unknown.');
      }
      item.coreType = coreType;
      item.cores = requireInteger(raw.cores, 'Container vCPU count', 1, 64);
      item.mem = requireInteger(raw.mem, 'Container memory', 1, 256);
      item.replicas = requireInteger(raw.replicas, 'Container replica count', 1, 99);
      item.baseMonthly = roundMoney(
        (CATALOG[coreType] * item.cores + CATALOG.mem * item.mem) * item.replicas,
      );
      break;
    }
    case 'node':
      item.count = requireInteger(raw.count, 'Dedicated node count', 1, 99);
      item.baseMonthly = roundMoney(CATALOG.node * item.count);
      break;
    case 'block':
      item.gib = requireInteger(raw.gib, 'Block storage capacity', 10, 4_000, 10);
      item.baseMonthly = roundMoney(CATALOG.blk * item.gib);
      break;
    case 'object':
      item.gib = requireInteger(raw.gib, 'Object storage capacity', 50, 10_000, 50);
      item.baseMonthly = roundMoney(CATALOG.obj * item.gib);
      break;
    case 'ip':
      item.count = requireInteger(raw.count, 'Public IPv4 count', 1, 99);
      item.baseMonthly = roundMoney(CATALOG.ip * item.count);
      break;
  }
  return item;
};

export interface IQuoteCalculation {
  stack: interfaces.IQuoteStackItem[];
  totals: interfaces.IQuoteTotals;
  demand: interfaces.IQuoteDemandContext;
  d: (typeof DEMAND)[typeof activeDemandKey];
  billBase: number;
  billSurged: number;
  surcharge: number;
  billQuoted: number;
  gaRef: number;
  premium: number;
  resQuoted: number;
  billCount: number;
  resCount: number;
}

/** Strictly validates a selection and computes every commercial field canonically. */
export const calculateQuote = (
  stackArg: unknown,
  commitmentTermArg: unknown,
): IQuoteCalculation => {
  if (!Array.isArray(stackArg) || stackArg.length === 0) {
    throw new Error('The composed stack is empty.');
  }
  if (stackArg.length > maximumStackItems) {
    throw new Error('The composed stack is too large.');
  }
  const commitmentTerm = requireCommitmentTerm(commitmentTermArg);
  const stack = stackArg.map((itemArg) => calculateQuoteLine(itemArg));
  const demandConfig = DEMAND[activeDemandKey];
  const billable = stack.filter((itemArg) => itemArg.regionState === 'live');
  const reserved = stack.filter((itemArg) => itemArg.regionState === 'build');
  const billBase = roundMoney(
    billable.reduce((totalArg, itemArg) => totalArg + itemArg.baseMonthly, 0),
  );
  const reservedBase = roundMoney(
    reserved.reduce((totalArg, itemArg) => totalArg + itemArg.baseMonthly, 0),
  );
  const billSurged = roundMoney(billBase * demandConfig.idx);
  const surcharge = roundMoney(billSurged - billBase);
  const billQuoted = roundMoney(billSurged * (1 - commitmentTerm));
  const gaRef = roundMoney(billBase * (1 - commitmentTerm));
  const premium = roundMoney(billQuoted - gaRef);
  const resQuoted = roundMoney(reservedBase * (1 - commitmentTerm));
  const demand: interfaces.IQuoteDemandContext = {
    key: activeDemandKey,
    multiplier: demandConfig.idx,
    utilization: demandConfig.util,
    tag: demandConfig.tag,
  };
  const totals: interfaces.IQuoteTotals = {
    billableComponents: billable.length,
    reservedComponents: reserved.length,
    listSubtotal: billBase,
    demandSurcharge: surcharge,
    quotedMonthly: billQuoted,
    gaListReference: gaRef,
    demandPremium: premium,
    reservedSubtotal: resQuoted,
  };
  return {
    stack,
    totals,
    demand,
    d: demandConfig,
    billBase,
    billSurged,
    surcharge,
    billQuoted,
    gaRef,
    premium,
    resQuoted,
    billCount: billable.length,
    resCount: reserved.length,
  };
};
